/* eslint-disable max-len */
/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import { omit } from 'lodash';
import { ACCESS_LEVEL, PERMISSION_KEYS } from 'utils/constants';
import { useAdminParams, compareArray } from 'utils/helpers';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { Logs, ACCOUNT_USER } from 'utils/types';
import Cookies from 'js-cookie';

import {
  Actor,
  ActivityLogData,
  ActivityLogDataArray,
  ActivityLogDataDetails,
} from '../ActivityLog';

const LeadLogs = {
  LEAD_CREATED: {
    title: 'Create Lead',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> created the lead.
      </p>
    ),
  },
  LEAD_UPDATED: {
    title: 'Edit Lead',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const _meta = omit(meta, ['updatedAt', 'accountId', 'companyId', 'data']);
      const _prevMeta = omit(prevMeta, [
        'updatedAt',
        'accountId',
        'companyId',
        'data',
      ]);

      const data = {
        ..._meta,
        tagId:
          _meta?.tagId?.map(({ tag }: Record<string, any>) => tag).join(', ') ??
          [],
        accountStatusId: _meta?.accountStatusId?.name,
      };

      const prevData = {
        ..._prevMeta,
        tagId:
          _prevMeta?.tagId
            ?.map(({ tag }: Record<string, any>) => tag)
            .join(', ') ?? [],
        accountStatusId: _prevMeta?.accountStatusId?.name,
      };

      return (
        <>
          <p>
            <Actor data={actor} /> updated the lead.
          </p>
          <ActivityLogData data={data} prevData={prevData} />
        </>
      );
    },
  },
  LEAD_DELETED: {
    title: 'Delete Lead',
    element: ({ actor }: Logs) => (
      <p>
        <Actor data={actor} /> deleted the lead.
      </p>
    ),
  },
  LEAD_ASSIGNED: {
    title: 'Associate Lead',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account') return '/settings/teams/people?id=';

        return `/preview/accounts/${accountId}/users/${accountUserId}/settings/teams/people?id=`;
      }, [actor?.role, role, actor?.actorId, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      const _accountUserId = prevMeta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );
      const _newAccountUserId = meta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );

      const { new: newArray } = compareArray(_accountUserId, _newAccountUserId);

      return (
        <p>
          <Actor data={actor} /> assgined the Lead to{' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={meta.accountUserId.filter(({ _id }: ACCOUNT_USER) =>
              newArray?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  LEAD_REMOVED_ASSIGNED: {
    title: 'Remove Associate Lead',
    element: ({ actor, meta, prevMeta }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account') return '/settings/teams/people?id=';

        return `/preview/accounts/${accountId}/users/${accountUserId}/settings/teams/people?id=`;
      }, [actor?.role, role, actor?.actorId, accountId, accountUserId]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      const _accountUserId = prevMeta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );
      const _newAccountUserId = meta.accountUserId.map(
        ({ _id }: ACCOUNT_USER) => _id.toString(),
      );

      const { remove } = compareArray(_accountUserId, _newAccountUserId);

      return (
        <p>
          <Actor data={actor} /> unassgined the Lead from{' '}
          <ActivityLogDataArray
            disabled={disabled}
            data={prevMeta.accountUserId.filter(({ _id }: ACCOUNT_USER) =>
              remove?.includes(_id),
            )}
            link={link}
          />
          .
        </p>
      );
    },
  },
  LEAD_DEAL_ASSIGNED: {
    title: 'Associate Lead to Deal',
    element: ({ actor, meta: { deal } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/sales/deals/${deal?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?._id}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${deal?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?._id}`;
      }, [actor?.role, role, accountId, accountUserId, deal]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> assgined the Lead to
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  LEAD_DEAL_REMOVED_ASSIGNED: {
    title: 'Remove Associate Lead to Deal',
    element: ({ actor, meta: { deal } }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const canViewLeadsAll = useCanRolePermissions(
        ACCESS_LEVEL.ACCOUNT_ADMIN,
        PERMISSION_KEYS.LEADS,
      );

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/sales/deals/${deal?._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?._id}`;
        }

        if (actor?.role === 'account') return `/sales/deals/${deal?._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/sales/deals/${deal?._id}`;
      }, [actor?.role, role, accountId, accountUserId, deal]);

      const disabled: boolean = useMemo(() => {
        if (actor?.role === 'admin') return false;

        return !canViewLeadsAll;
      }, [actor?.role, canViewLeadsAll]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> unassgined the Lead from Deal
          </p>
          <ActivityLogDataDetails link={link} disabled={disabled} />
        </div>
      );
    },
  },
  LEAD_COMMENT_ADDED: {
    title: 'Add Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_COMMENT_ADDED: {
    title: 'Add Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> commented to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_UPDATED: {
    title: 'Edit Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, referenceId, accountId, accountUserId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_COMMENT_UPDATED: {
    title: 'Edit Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> updated a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_DELETED: {
    title: 'Delete Note',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [actor?.role, role, referenceId, accountId, accountUserId, comment]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_COMMENT_DELETED: {
    title: 'Delete Comment',
    element: ({ actor, meta: { comment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${comment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${comment._id}`;
      }, [
        actor?.role,
        referenceId,
        role,
        actor?.actorId,
        accountId,
        accountUserId,
        comment,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> deleted a comment to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_ATTACHMENT_ADDED: {
    title: 'Add Lead Attachment',
    element: ({ actor, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/contact/leads/${referenceId}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}`;
        }

        if (actor?.role === 'account') return `/contact/leads/${referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> attached a file to a lead.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_ATTACHMENT_DELETED: {
    title: 'Delete Lead Attachment',
    element: ({ actor, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account') return `/contact/leads/${referenceId}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}`;
        }

        if (actor?.role === 'account') return `/contact/leads/${referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}`;
      }, [actor?.role, role, accountId, accountUserId, referenceId]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a lead.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        attachment._id,
        referenceId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Note Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

          return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        referenceId,
        role,
        accountId,
        attachment._id,
        accountUserId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a note.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_COMMENT_ATTACHMENT_ADDED: {
    title: 'Add Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        actor?.actorId,
        accountId,
        accountUserId,
        referenceId,
        role,
        attachment._id,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> added attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
  LEAD_COMMENT_COMMENT_ATTACHMENT_DELETED: {
    title: 'Delete Comment Attachment',
    element: ({ actor, meta: { attachment }, referenceId }: Logs) => {
      const { accountId, accountUserId } = useAdminParams();
      const role = Cookies.get('_role');

      const link = useMemo(() => {
        if (!actor?.role) {
          if (role === 'account')
            return `/settings/teams/people?id=${actor?.actorId}`;

          return `/preview/account/${accountId}/user/${accountUserId}/settings/teams/people?id=${actor?.actorId}`;
        }

        if (actor?.role === 'account')
          return `/contact/leads/${referenceId}/notes?id=${attachment._id}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/contact/leads/${referenceId}/notes?id=${attachment._id}`;
      }, [
        actor?.role,
        role,
        accountId,
        accountUserId,
        attachment._id,
        referenceId,
        actor?.actorId,
      ]);

      return (
        <div className="flex items-center space-x-2">
          <p>
            <Actor data={actor} /> removed attachment file to a comment.
          </p>
          <ActivityLogDataDetails link={link} disabled={false} />
        </div>
      );
    },
  },
};

export default LeadLogs;
